import React, { createContext, useContext, useState, useEffect } from 'react';
import accountService from '../services/accountService';
import api from '../services/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [appUser, setAppUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    initAuth();
  }, []);

  const initAuth = async () => {
    if (accountService.isAuthenticated()) {
      try {
        const validatedUser = await accountService.validateToken();
        if (validatedUser) {
          setUser(validatedUser);
          // Initialize app-level role
          try {
            const { user: localUser } = await api.initUser();
            setAppUser(localUser);
          } catch (err) {
            console.error('App user init error:', err);
          }
        } else {
          accountService.clearAuth();
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        accountService.clearAuth();
      }
    }
    setLoading(false);
  };

  const login = async (email, password) => {
    const u = await accountService.login(email, password);
    setUser(u);
    try {
      const { user: localUser } = await api.initUser();
      setAppUser(localUser);
    } catch {}
    return u;
  };

  const register = async (email, password, firstName, lastName, phoneNumber) => {
    return accountService.register(email, password, firstName, lastName, phoneNumber);
  };

  const logout = async () => {
    await accountService.logout();
    setUser(null);
    setAppUser(null);
  };

  const role = appUser?.role || null;
  const isSuperAdmin = role === 'superadmin';
  const isAdmin = role === 'superadmin' || role === 'admin';
  const isEditor = isAdmin || role === 'editor';

  const value = {
    user,
    appUser,
    loading,
    isAuthenticated: !!user,
    role,
    isSuperAdmin,
    isAdmin,
    isEditor,
    login,
    register,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
